/**
 * Date/time formatting helpers used across admin pages.
 *
 * Every timestamp surfaced to operators renders in Pacific Time. The
 * IANA zone `America/Los_Angeles` is used (not a hardcoded "PST") so
 * Daylight Saving Time is handled automatically — PT shows as PDT
 * in summer and PST in winter without operator intervention.
 */

const PACIFIC_ZONE = 'America/Los_Angeles';

/**
 * Format an ISO timestamp string as a US-style date + time in Pacific
 * Time. Example output: "5/19/2026, 3:00:22 PM PDT".
 *
 * Returns the empty string for null/undefined/falsy input so callers
 * can drop in without nullish-checking every site.
 */
export function formatPacific(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    timeZone: PACIFIC_ZONE,
    timeZoneName: 'short',
  });
}

/**
 * Same shape as formatPacific, but in the runtime's own time zone. On the
 * client that is the viewer's zone (see components/shared/LocalTime.tsx);
 * on the server it would be the server's zone, so don't call it there.
 */
export function formatLocal(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', { timeZoneName: 'short' });
}

/**
 * Same as formatPacific but without the trailing "PDT"/"PST" suffix.
 * Use where the timezone label would be noisy (e.g. compact tables
 * with many timestamps in the same column).
 */
export function formatPacificCompact(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', { timeZone: PACIFIC_ZONE });
}

/** Pacific-Time calendar date only, e.g. "10/31/2026". Empty for falsy input. */
export function formatPacificDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { timeZone: PACIFIC_ZONE });
}
